import { RefObject, useCallback, useRef } from "react";
import { fetchBrands } from "../../../../services/brandService";
import { fetchMediaByCustomer } from "../../../../services/contentService";
import { MediaItem } from "../../../../types/MediaItem";
import { GeneratedPostsData, Message } from "../types";
import { debug } from "../../../../utils/debugLogger";
import {
  patchGeneratedPostsByIndex,
  patchGeneratedPostsAppendCarouselSlides,
  patchGeneratedPostsSetPdfSlides,
  mediaItemToCarouselSlide,
  mediaToPdfSlide,
} from "../utils/generatedPostsPatchers";
import { uploadMedia } from "../../../../features/media/utils/mediaUploadUtils";

type UseAttachmentMediaParams = {
  customerId?: string;
  idToken: string;
  userName?: string;
  fileInputRef: RefObject<HTMLInputElement>;
  /** Hidden input for social-post media (image + video); separate from chat image-only input. */
  postMediaFileInputRef: RefObject<HTMLInputElement | null>;
  pendingAttachPostIndex: number | null;
  pendingImageForPostTempId: string | null;
  /** When set, My Library is in Instagram carousel multi-select mode. */
  pendingCarouselForPostTempId: string | null;
  /** When set, My Library is in LinkedIn multi-PDF multi-select mode. */
  pendingPdfCarouselForPostTempId: string | null;
  setShowAttachmentMenu: (value: boolean) => void;
  setShowMediaLibrary: (value: boolean) => void;
  setMediaLibraryItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  setLoadingMedia: (value: boolean) => void;
  setLogoLoading: (value: boolean) => void;
  setSelectedImages: React.Dispatch<React.SetStateAction<MediaItem[]>>;
  setPendingAttachPostIndex: (value: number | null) => void;
  setPendingImageForPostTempId: (value: string | null) => void;
  setPendingCarouselForPostTempId: (value: string | null) => void;
  setPendingPdfCarouselForPostTempId: (value: string | null) => void;
  setStreamingGeneratedPosts: React.Dispatch<
    React.SetStateAction<GeneratedPostsData | null>
  >;
  setPanelSocialPosts: React.Dispatch<React.SetStateAction<GeneratedPostsData | null>>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setError: (value: string | null) => void;
};

export function useAttachmentMedia({
  customerId,
  idToken,
  userName,
  fileInputRef,
  postMediaFileInputRef,
  pendingAttachPostIndex,
  pendingImageForPostTempId,
  pendingCarouselForPostTempId,
  pendingPdfCarouselForPostTempId,
  setShowAttachmentMenu,
  setShowMediaLibrary,
  setMediaLibraryItems,
  setLoadingMedia,
  setLogoLoading,
  setSelectedImages,
  setPendingAttachPostIndex,
  setPendingImageForPostTempId,
  setPendingCarouselForPostTempId,
  setPendingPdfCarouselForPostTempId,
  setStreamingGeneratedPosts,
  setPanelSocialPosts,
  setMessages,
  setError,
}: UseAttachmentMediaParams) {
  /** Set right before opening post file picker so temp id is available synchronously on change. */
  const postMediaPickTempIdRef = useRef<string | null>(null);

  // Click-outside for the attachment menu lives in ChatInputArea only: the menu is
  // portaled to document.body (attachmentsPortalRef). A handler here that only checks
  // attachmentMenuRef would treat every portal click as "outside" and close the menu
  // on mousedown before My Library / Local files onClick runs.

  const loadMediaLibrary = useCallback(async (opts?: { includeVideo?: boolean; mediaType?: "image" | "video" | "document" | "all" }) => {
    if (!customerId || !idToken) return;
    setLoadingMedia(true);
    setError(null);
    try {
      const mediaType = opts?.mediaType;
      const includeVideo = opts?.includeVideo ?? false;
      const resolvedType: "image" | "video" | "document" | undefined =
        mediaType === "video"
          ? "video"
          : mediaType === "document"
            ? "document"
            : mediaType === "all" || includeVideo
              ? undefined
              : "image";
      const { medias } = await fetchMediaByCustomer(customerId, idToken, {
        limit: 50,
        mediaType: resolvedType,
      });
      setMediaLibraryItems(medias);
    } catch (err) {
      debug.error("Error fetching media:", err);
      setError(err instanceof Error ? err.message : "Failed to load media library");
    } finally {
      setLoadingMedia(false);
    }
  }, [customerId, idToken, setError, setLoadingMedia, setMediaLibraryItems]);

  const handleGallery = () => {
    setShowAttachmentMenu(false);
    fileInputRef.current?.click();
  };

  const applyMediaUrlToPost = useCallback(
    (tempId: string, mediaUrl: string, mediaKind: "image" | "video" | "document" = "image") => {
      const patchPost = (p: GeneratedPostsData["posts"][number]) => {
        if (p.temp_id !== tempId) return p;
        if (mediaKind === "document") {
          // LinkedIn document post (SGL-174): document_url replaces image/video.
          return {
            ...p,
            document_url: mediaUrl,
            image_url: "",
            video_url: "",
            carousel_slides: undefined,
          };
        }
        if (mediaKind === "video") {
          return {
            ...p,
            video_url: mediaUrl,
            image_url: "",
            document_url: "",
            instagram_content_format: String(p.platform ?? "").toUpperCase().includes("INSTAGRAM")
              ? "video"
              : p.instagram_content_format,
            carousel_slides: undefined,
          };
        }
        return {
          ...p,
          image_url: mediaUrl,
          video_url: "",
          document_url: "",
          instagram_content_format: String(p.platform ?? "").toUpperCase().includes("INSTAGRAM")
            ? "image"
            : p.instagram_content_format,
          carousel_slides: undefined,
        };
      };

      setPanelSocialPosts((prev) => {
        if (!prev?.posts?.length) return prev;
        return { posts: prev.posts.map(patchPost) };
      });
      setStreamingGeneratedPosts((prev) => {
        if (!prev?.posts?.length) return prev;
        return { posts: prev.posts.map(patchPost) };
      });
      setMessages((prev) =>
        prev.map((m) => {
          if (!m.generatedPosts?.posts?.some((p) => p.temp_id === tempId)) return m;
          return {
            ...m,
            generatedPosts: { posts: m.generatedPosts.posts.map(patchPost) },
          };
        })
      );
      setPendingImageForPostTempId(null);
    },
    [
      setMessages,
      setPanelSocialPosts,
      setPendingImageForPostTempId,
      setStreamingGeneratedPosts,
    ]
  );

  /** Library flow sets `pendingImageForPostTempId`; local pick uses only the ref so cancel does not leave stale pending state. */
  const openLocalFileForPost = useCallback(
    (tempId: string) => {
      postMediaPickTempIdRef.current = tempId;
      postMediaFileInputRef.current?.click();
    },
    [postMediaFileInputRef]
  );

  /**
   * Local file pick for a social post card. Shows an instant base64 preview
   * (some browsers omit `file.type`, so don't pre-filter on it — let
   * uploadMedia's extension fallback decide), then replaces it with the real
   * CloudFront URL once the upload finishes. Posts publish through the
   * platforms' own APIs, which need a fetchable URL — a data: URI left in
   * image_url would silently fail at publish time.
   */
  const handlePostMediaFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tempId = postMediaPickTempIdRef.current ?? pendingImageForPostTempId;
    postMediaPickTempIdRef.current = null;

    // input.files is live: grab the File itself before resetting .value, or
    // the reset clears files too and every pick silently no-ops.
    const file = event.target.files?.[0] ?? null;
    if (postMediaFileInputRef.current) postMediaFileInputRef.current.value = "";
    if (!file || tempId === null) return;

    if (file.type.startsWith("video/")) {
      // Base64-encoding a multi-MB video blocks the main thread; an object
      // URL is instant and gets replaced by the real CloudFront URL below.
      applyMediaUrlToPost(tempId, URL.createObjectURL(file), "video");
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target?.result as string;
        applyMediaUrlToPost(tempId, previewUrl, "image");
      };
      reader.readAsDataURL(file);
    }

    if (!customerId) return;

    uploadMedia({
      file,
      customerId,
      userEmail: userName || "",
      token: idToken,
      tags: ["social-post"],
    })
      .then((result) => {
        if (result.success && result.cloudFrontUrl) {
          applyMediaUrlToPost(tempId, result.cloudFrontUrl, result.mediaKind === "video" ? "video" : "image");
        } else {
          setError(result.errorMessage || "Failed to upload media. Please try again.");
        }
      })
      .catch((err) => {
        debug.error("Error uploading post media:", err);
        setError("Failed to upload media. Please try again.");
      });
  };

  /**
   * Shared by local-file picks and Google Drive imports: image → upload + preview via S3;
   * any other file (PDF, DOCX, JSON, etc.) → base64 data URL sent later as chat input_file.
   */
  const processIncomingFile = useCallback(
    (file: File) => {
      const isImage = file.type.startsWith("image/");
      const reader = new FileReader();

      if (isImage) {
        reader.onload = async (e) => {
          const previewUrl = e.target?.result as string;
          const tempId = `local-${Date.now()}-${Math.random()}`;

          // Show local preview immediately while uploading
          const placeholder: MediaItem = {
            media_id: tempId,
            cloudfront_url: previewUrl,
            s3_key: file.name,
            media_type: "IMAGE",
            created_at: new Date().toISOString(),
            processed: false,
            metadata: { size: file.size, dimensions: "" },
            tags: [],
          };
          setSelectedImages((prev) => [...prev, placeholder]);

          if (!customerId) return;

          try {
            const result = await uploadMedia({
              file,
              customerId,
              userEmail: userName || "",
              token: idToken,
              tags: ["chat-reference"],
            });

            if (result.success && result.mediaId && result.cloudFrontUrl) {
              setSelectedImages((prev) =>
                prev.map((img) =>
                  img.media_id === tempId
                    ? {
                        ...img,
                        media_id: result.mediaId!,
                        cloudfront_url: result.cloudFrontUrl,
                        s3_key: result.s3Key || img.s3_key,
                        processed: true,
                      }
                    : img
                )
              );
            } else {
              setSelectedImages((prev) => prev.filter((img) => img.media_id !== tempId));
              setError(result.errorMessage || "Failed to upload image. Please try from your library.");
            }
          } catch (err) {
            debug.error("Error uploading chat reference image:", err);
            setSelectedImages((prev) => prev.filter((img) => img.media_id !== tempId));
            setError("Failed to upload image. Please try again or select from your library.");
          }
        };
      } else {
        // Non-image files (PDF, DOCX, JSON, etc.) — send as base64 data URL via input_file
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          const fileItem: MediaItem = {
            media_id: `local-${Date.now()}-${Math.random()}`,
            cloudfront_url: dataUrl,
            s3_key: file.name,
            media_type: file.type || "application/octet-stream",
            created_at: new Date().toISOString(),
            processed: false,
            metadata: { size: file.size, dimensions: "" },
            tags: [],
          };
          setSelectedImages((prev) => [...prev, fileItem]);
        };
      }

      reader.readAsDataURL(file);
    },
    [customerId, idToken, userName, setSelectedImages, setError]
  );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach(processIncomingFile);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /** Files downloaded client-side from Google Drive; same handling as a local file pick. */
  const handleGoogleDriveFiles = useCallback(
    (files: File[]) => {
      files.forEach(processIncomingFile);
    },
    [processIncomingFile]
  );

  const applyCarouselFromLibrary = useCallback(
    (tempId: string, items: MediaItem[]) => {
      const slides = items
        .map(mediaItemToCarouselSlide)
        .filter((s): s is NonNullable<typeof s> => s != null);
      setPanelSocialPosts((prev) =>
        patchGeneratedPostsAppendCarouselSlides(prev, tempId, slides)
      );
      setStreamingGeneratedPosts((prev) =>
        patchGeneratedPostsAppendCarouselSlides(prev, tempId, slides)
      );
      setMessages((prev) =>
        prev.map((m) => {
          if (!m.generatedPosts?.posts?.some((p) => p.temp_id === tempId)) return m;
          const next = patchGeneratedPostsAppendCarouselSlides(m.generatedPosts, tempId, slides);
          return next ? { ...m, generatedPosts: next } : m;
        })
      );
    },
    [setMessages, setPanelSocialPosts, setStreamingGeneratedPosts]
  );

  const handleImageSelect = (image: MediaItem) => {
    // PDF multi-select mode: toggle selection
    if (pendingPdfCarouselForPostTempId !== null) {
      setSelectedImages((prev) => {
        const exists = prev.some((img) => img.media_id === image.media_id);
        if (exists) return prev.filter((img) => img.media_id !== image.media_id);
        return [...prev, image];
      });
      return;
    }

    if (pendingCarouselForPostTempId !== null) {
      setSelectedImages((prev) => {
        const exists = prev.some((img) => img.media_id === image.media_id);
        if (exists) return prev.filter((img) => img.media_id !== image.media_id);
        return [...prev, image];
      });
      return;
    }

    if (pendingImageForPostTempId !== null) {
      const url = image.cloudfront_url || "";
      const tempId = pendingImageForPostTempId;
      const mt = image.media_type.toLowerCase();
      const mediaKind: "image" | "video" | "document" =
        mt === "document" || image.s3_key?.toLowerCase().endsWith(".pdf")
          ? "document"
          : mt === "video"
            ? "video"
            : "image";
      applyMediaUrlToPost(tempId, url, mediaKind);
      setShowMediaLibrary(false);
      return;
    }

    if (pendingAttachPostIndex !== null) {
      const url = image.cloudfront_url || "";
      setStreamingGeneratedPosts((prev) => patchGeneratedPostsByIndex(prev, pendingAttachPostIndex, url) ?? null);
      setMessages((prev) => {
        const lastAssistantIdx = [...prev]
          .reverse()
          .findIndex((m) => m.role === "assistant" && m.generatedPosts?.posts?.length);
        if (lastAssistantIdx === -1) return prev;
        const idx = prev.length - 1 - lastAssistantIdx;
        const msg = prev[idx];
        if (!msg.generatedPosts) return prev;
        const next = patchGeneratedPostsByIndex(msg.generatedPosts, pendingAttachPostIndex, url);
        return prev.map((m, i) => (i === idx && next ? { ...m, generatedPosts: next } : m));
      });
      setPendingAttachPostIndex(null);
      setShowMediaLibrary(false);
      return;
    }

    setSelectedImages((prev) => {
      if (prev.some((img) => img.media_id === image.media_id)) return prev;
      return [...prev, image];
    });
  };

  const handleRemoveImage = (imageId: string) => {
    setSelectedImages((prev) => prev.filter((img) => img.media_id !== imageId));
  };

  const handleCloseMediaLibrary = () => {
    setShowMediaLibrary(false);
    setMediaLibraryItems([]);
    setPendingAttachPostIndex(null);
    setPendingImageForPostTempId(null);
    setPendingCarouselForPostTempId(null);
    setPendingPdfCarouselForPostTempId(null);
    postMediaPickTempIdRef.current = null;
  };

  const handleMediaLibraryDone = () => {
    const tempId = pendingCarouselForPostTempId;
    setSelectedImages((imgs) => {
      if (tempId !== null && imgs.length > 0) {
        applyCarouselFromLibrary(tempId, imgs);
      }
      return [];
    });
    setPendingCarouselForPostTempId(null);
    setShowMediaLibrary(false);
    setMediaLibraryItems([]);
  };

  /** LinkedIn multi-PDF: commit selected PDFs as carousel_slides of type "PDF". */
  const handlePdfCarouselDone = () => {
    const tempId = pendingPdfCarouselForPostTempId;
    setSelectedImages((imgs) => {
      if (tempId !== null && imgs.length > 0) {
        const slides = imgs
          .map(mediaToPdfSlide)
          .filter((s): s is NonNullable<typeof s> => s != null);
        const patch = (prev: import("../types").GeneratedPostsData | null) =>
          patchGeneratedPostsSetPdfSlides(prev, tempId, slides);
        setPanelSocialPosts((prev) => patch(prev));
        setStreamingGeneratedPosts((prev) => patch(prev));
        setMessages((prev) =>
          prev.map((m) => {
            if (!m.generatedPosts?.posts?.some((p) => p.temp_id === tempId)) return m;
            const next = patchGeneratedPostsSetPdfSlides(m.generatedPosts, tempId, slides);
            return next ? { ...m, generatedPosts: next } : m;
          })
        );
      }
      return [];
    });
    setPendingPdfCarouselForPostTempId(null);
    setShowMediaLibrary(false);
    setMediaLibraryItems([]);
  };

  const handleUseLogo = async (explicitPostTempId?: string | null) => {
    if (!customerId) {
      setError("Please select a customer first");
      return;
    }

    const postTempId = explicitPostTempId ?? pendingImageForPostTempId;

    try {
      setLogoLoading(true);
      const brandResponse = await fetchBrands(customerId, idToken);
      const brandData = brandResponse?.brands?.[0];
      if (!brandData?.visual?.logo_url) {
        setError("No logo found for this brand. Please add one in Brand Management.");
        return;
      }

      const logoUrl = brandData.visual.logo_url;

      if (postTempId !== null) {
        applyMediaUrlToPost(postTempId, logoUrl);
        setShowAttachmentMenu(false);
        return;
      }

      const logoItem: MediaItem = {
        media_id: brandData.visual.logo_media_id || `logo_${customerId}`,
        cloudfront_url: logoUrl,
        media_type: "IMAGE",
        s3_key: `${customerId}/logo`,
        processed: true,
        metadata: { size: 0, dimensions: "unknown" },
        tags: ["logo", "brand"],
        created_at: new Date().toISOString(),
        created_by: userName || "user",
      };
      setSelectedImages((prev) =>
        prev.some((img) => img.media_id === logoItem.media_id) ? prev : [...prev, logoItem]
      );
      setShowAttachmentMenu(false);
    } catch (err) {
      debug.error("Error fetching brand logo:", err);
      setError("Failed to load brand logo. Please try again.");
    } finally {
      setLogoLoading(false);
    }
  };

  return {
    loadMediaLibrary,
    handleGallery,
    handleFileSelect,
    handleGoogleDriveFiles,
    handlePostMediaFileSelect,
    openLocalFileForPost,
    handleImageSelect,
    handleRemoveImage,
    handleCloseMediaLibrary,
    handleMediaLibraryDone,
    handlePdfCarouselDone,
    handleUseLogo,
  };
}
